import React from 'react';
import { useTranslation } from 'react-i18next';

import { Tooltip } from 'components';
import Dropdown from 'components/form/Dropdown';
import type { PropertyUIType } from 'lane-shared/types/properties/PropertyUI';

import infoCircle from 'static/img/info-circle.svg';

import styles from './BraxosConnectionMode.scss';

const TOOLTIP_TRANSLATION_KEY =
  'web.admin.channel.integrations.access.access-control-settings.braxos.connection-mode.description';

export type BraxosConnectionModeOption = {
  _id: string;
  _order: number;
  name: string;
  value: string;
};

type BraxosConnectionModeProps = {
  property: PropertyUIType;
  value: BraxosConnectionModeOption | string | undefined;
  onChange: (value: BraxosConnectionModeOption | undefined) => void;
  disabled?: boolean;
  label?: string;
};

function readOptions(
  property: PropertyUIType
): BraxosConnectionModeOption[] {
  const inValidator = property.validators?.find(
    validator => validator.name === 'In'
  );
  const rawValue = inValidator?.value;

  return Array.isArray(rawValue) ? rawValue : [];
}

function readSelectedId(
  value: BraxosConnectionModeOption | string | undefined
): string | undefined {
  if (typeof value === 'string') {
    return value;
  }

  return value?._id;
}

export const BraxosConnectionMode = ({
  property,
  value,
  onChange,
  disabled,
  label,
}: BraxosConnectionModeProps) => {
  const { t } = useTranslation();

  const options = readOptions(property);
  const selectedId = readSelectedId(value);
  const items = options.map(option => ({
    value: option._id,
    label: option.name,
  }));

  return (
    <div className={styles.wrapper}>
      <div className={styles.labelRow}>
        <label className={styles.label}>{t(label || '')}</label>
        <Tooltip
          TooltipComponent={t(TOOLTIP_TRANSLATION_KEY)}
          zIndex={1000}
        >
          <img
            src={infoCircle}
            alt="info"
            className={styles.icon}
          />
        </Tooltip>
      </div>
      <Dropdown
        disabled={disabled}
        className={styles.dropdown}
        value={selectedId}
        items={items}
        onValueChange={selectedValue =>
          onChange(options.find(option => option._id === selectedValue))
        }
      />
    </div>
  );
};
